import { Component, EventEmitter, Output, ViewEncapsulation } from '@angular/core';
import { LcsNativeDateAdapter } from '@lcs/shared/core';
import { LcsDateRangePickerInputPanelContentFormValue, LcsDateRangePickerOption } from '@lcs/shared/datepicker';
import { Store } from '@ngrx/store';
import { BlockedIpStatuses, BlockedIpTypes, RelativeDateOptions } from 'generated/graphql-types';
import { LcsDateTimeAdapter } from 'src/app/common-components/date-time-picker/configuration/date-time-adapter';
import { EntityRegisterView } from 'src/app/common-components/register/models/register-view';
import { dateRangePickerOptionsForCallHistory } from 'src/app/date-picker/date-picker-options';
import {
   BannedProvisioningIpOrderingOptions,
   BannedProvisioningIpRegisterOptions,
} from '../models/banned-provisioning-ip-register-options';
import { BannedProvisioningIpRegisterListItem } from '../models/banned-provisioning-ip-register-results.model';
import { selectQuickFilters } from '../state/banned-provisioning-ip-register.reducer';

@Component({
   encapsulation: ViewEncapsulation.None,
   selector: 'ndt-banned-provisioning-ip-register-view',
   styleUrls: ['./banned-provisioning-ip-register-view.component.scss'],
   templateUrl: './banned-provisioning-ip-register-view.component.html',
   standalone: false,
})
export class BannedProvisioningIpRegisterView extends EntityRegisterView<
   BannedProvisioningIpRegisterListItem,
   BannedProvisioningIpRegisterOptions,
   BannedProvisioningIpOrderingOptions
> {
   protected selectQuickFilters(): (state: object) => BannedProvisioningIpRegisterOptions | null {
      return selectQuickFilters;
   }

   @Output() unbanIp = new EventEmitter<BannedProvisioningIpRegisterListItem>();

   @Output() allowIp = new EventEmitter<BannedProvisioningIpRegisterListItem>();

   readonly blockedIpTypeLabels: { [key in BlockedIpTypes]: string } = {
      [BlockedIpTypes.Fail2Ban]: 'Provisioning Server Firewall',
      [BlockedIpTypes.NDTBlocklist]: 'NDT Firewall',
      [BlockedIpTypes.GlobalBlocklist]: 'Global LCS Firewall',
   };

   readonly blockedIpStatusLabels: { [key in BlockedIpStatuses]: string } = {
      [BlockedIpStatuses.Banned]: 'Banned',
      [BlockedIpStatuses.Requested]: 'Requested',
      [BlockedIpStatuses.Unbanning]: 'Unbanning',
   };

   readonly blockedIpTypes = [BlockedIpTypes.Fail2Ban, BlockedIpTypes.NDTBlocklist, BlockedIpTypes.GlobalBlocklist];

   readonly blockedIpStatuses =  [BlockedIpStatuses.Banned,BlockedIpStatuses.Requested, BlockedIpStatuses.Unbanning];

   selectedBlockedIpType: BlockedIpTypes | null = null;

   selectedBlockedIpStatus: BlockedIpStatuses | null = null;

   startDate: Date | null = null;

   endDate: Date | null = null;

   dateRangePickerOptions: LcsDateRangePickerOption<Date>[] = [];

   initialDateRange: LcsDateRangePickerInputPanelContentFormValue<Date> | undefined;

   constructor(store: Store, private dateAdapter: LcsDateTimeAdapter<Date>) {
      super(store);

      this.dateRangePickerOptions = dateRangePickerOptionsForCallHistory(new LcsNativeDateAdapter(this.dateAdapter));
      this.initialDateRange = this.getInitialDateRange(RelativeDateOptions.Today);
   }

   getRouterLink(id: number): string {
      return ''; //No routing on this register
   }

   blockedIpTypeDisplayWith = (blockedIpType: BlockedIpTypes | null): string => {
      return blockedIpType ? this.blockedIpTypeLabels[blockedIpType] : '';
   };

   blockedIpStatusDisplayWith = (blockedIpStatus: BlockedIpStatuses | null): string => {
      return blockedIpStatus ? this.blockedIpStatusLabels[blockedIpStatus] : '';
   };

   protected override onQuickFiltersInit(quickFilters: BannedProvisioningIpRegisterOptions): void {
      super.onQuickFiltersInit(quickFilters);

      this.endDate = quickFilters.endDate ? new Date(quickFilters.endDate) : null;
      this.startDate = quickFilters.startDate ? new Date(quickFilters.startDate) : null;
      this.selectedBlockedIpStatus = quickFilters.status ?? null;
      this.selectedBlockedIpType = quickFilters.type ?? null;
   }

   canUnbanOrAllow(bannedIp: BannedProvisioningIpRegisterListItem): boolean {
      return bannedIp.status === BlockedIpStatuses.Banned;
   }

   onAllowClick(bannedIp: BannedProvisioningIpRegisterListItem): void {
      this.allowIp.emit(bannedIp);
   }

   onUnbanClick(bannedIp: BannedProvisioningIpRegisterListItem): void {
      this.unbanIp.emit(bannedIp);
   }

   onDateQuickFilterChange(event: { endDate: Date | undefined; startDate: Date | undefined }): void {
      this.endDate = event.endDate ?? null;
      this.startDate = event.startDate ?? null;
      this.registerOptions = {
         ...this.registerOptions,
         endDate: this.endDate,
         startDate: this.startDate,
      };
      this.quickFilterChange.emit(this.registerOptions);
   }

   onStatusSelected(status: BlockedIpStatuses | null): void {
      this.selectedBlockedIpStatus = status;
      this.registerOptions = {
         ...this.registerOptions,
         status,
      };
      this.quickFilterChange.emit(this.registerOptions);
   }

   onTypeSelected(type: BlockedIpTypes | null): void {
      this.selectedBlockedIpType = type;
      this.registerOptions = {
         ...this.registerOptions,
         type,
      };
      this.quickFilterChange.emit(this.registerOptions);
   }

   private getInitialDateRange(
      relativeDate: RelativeDateOptions | null
   ): LcsDateRangePickerInputPanelContentFormValue<Date> | undefined {
      const datePickerOption = this.dateRangePickerOptions.find((opt) => opt.id === relativeDate);
      if (!datePickerOption) {
         return undefined;
      }
      return {
         datePickerOption,
         dateRange: datePickerOption.callback(),
      };
   }
}
